import React, { useCallback, useEffect, useState } from 'react';
import { Loader2, Monitor, ShieldAlert, ShieldCheck, Trash2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * "My Devices" self-service card for /account.
 *
 * Shows every browser/OS the reader has signed in from, marks the current
 * device, and lets them revoke old ones so they stay inside the 3-device
 * limit. Also exposes any warning stamps the paywall has left on the
 * account so a reader can see exactly where they sit on the ladder before
 * a ban lands. Copy is explicit: sharing outside the 3 allowed devices
 * triggers warnings and, after 3 strikes, a permanent no-refund ban.
 */
const fmt = (iso) => {
  if (!iso) return '-';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('en-AU', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
};

const MyDevicesCard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [busyCid, setBusyCid] = useState('');

  const load = useCallback(async () => {
    setLoading(true); setErr('');
    try {
      const r = await fetch(`${API}/api/me/devices`, { credentials: 'include', cache: 'no-store' });
      if (!r.ok) throw new Error('Could not load your devices.');
      setData(await r.json());
    } catch (e) {
      setErr(e.message || 'Could not load your devices.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const revoke = async (cid) => {
    if (!window.confirm('Sign this device out of your account? It will need to log in again next time.')) return;
    setBusyCid(cid);
    try {
      const r = await fetch(`${API}/api/me/devices/revoke`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ client_id: cid }),
      });
      if (!r.ok) {
        const j = await r.json().catch(() => ({}));
        throw new Error(j.detail || 'Revoke failed.');
      }
      await load();
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusyCid('');
    }
  };

  const items = data?.items || [];
  const allowed = data?.allowed ?? 3;
  const active = data?.active_in_window ?? items.length;
  const overLimit = active > allowed;
  const warnings = data?.warnings || {};
  const strike =
    warnings.warning_2_at ? 2 :
    warnings.warning_1_at ? 1 :
    0;

  return (
    <section
      data-testid="my-devices-card"
      className="border border-zinc-800 rounded-lg bg-zinc-950 p-5 mb-6 space-y-4"
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-1">Security</p>
          <p className="text-white text-lg font-black flex items-center gap-2">
            <Monitor className="h-4 w-4" /> My Devices
          </p>
          <p className="text-zinc-400 text-xs mt-1 leading-relaxed" data-testid="my-devices-policy-copy">
            One account, up to <strong className="text-white">3 devices</strong> in any 14-day window
            (phone + laptop + tablet). <strong className="text-white">Any account sharing will result in
            your account being banned, without refund.</strong> You get <strong className="text-yellow-300">3
            chances</strong> before that happens - Warning 1 → Final Warning → Ban. Revoke anything you
            don&apos;t recognise.
          </p>
        </div>
        <button
          type="button"
          onClick={load}
          disabled={loading}
          data-testid="my-devices-refresh"
          className="shrink-0 text-[10px] font-black uppercase tracking-widest text-zinc-400 hover:text-white px-2 py-1.5 border border-zinc-800 hover:border-zinc-600 rounded"
        >
          {loading ? <Loader2 className="h-3 w-3 animate-spin inline" /> : 'Refresh'}
        </button>
      </div>

      {/* Explicit 3-strike ladder so a reader can see their exact standing.
          Colours light up as they progress: yellow → orange → red. */}
      <ol
        data-testid="my-devices-ladder"
        className="grid grid-cols-3 gap-2 text-[10px]"
      >
        <li className={
          'rounded border px-2 py-2 ' +
          (strike >= 1
            ? 'border-yellow-500/70 bg-yellow-950/40 text-yellow-200'
            : 'border-zinc-800 bg-zinc-950 text-zinc-500')
        }>
          <span className="font-black uppercase tracking-widest">1. Warning</span>
          <span className="block mt-0.5">
            {warnings.warning_1_at ? fmt(warnings.warning_1_at) : 'Not issued'}
          </span>
        </li>
        <li className={
          'rounded border px-2 py-2 ' +
          (strike >= 2
            ? 'border-orange-500/70 bg-orange-950/40 text-orange-200'
            : 'border-zinc-800 bg-zinc-950 text-zinc-500')
        }>
          <span className="font-black uppercase tracking-widest">2. Final Warning</span>
          <span className="block mt-0.5">
            {warnings.warning_2_at ? fmt(warnings.warning_2_at) : 'Not issued'}
          </span>
        </li>
        <li className={
          'rounded border px-2 py-2 ' +
          (strike >= 2
            ? 'border-red-500/70 bg-red-950/50 text-red-200'
            : 'border-zinc-800 bg-zinc-950 text-zinc-500')
        }>
          <span className="font-black uppercase tracking-widest">3. Ban · No Refund</span>
          <span className="block mt-0.5">
            {strike >= 2 ? 'One more device = ban' : 'Only if you keep going'}
          </span>
        </li>
      </ol>

      <div
        data-testid="my-devices-status-strip"
        className={
          'flex flex-wrap items-center gap-2 text-[11px] font-black uppercase tracking-widest ' +
          (overLimit || strike > 0 ? 'text-red-300' : 'text-emerald-300')
        }
      >
        {overLimit || strike > 0 ? <ShieldAlert className="h-3.5 w-3.5" /> : <ShieldCheck className="h-3.5 w-3.5" />}
        <span data-testid="my-devices-active-count">
          Active devices: {active} / {allowed}
        </span>
        {strike > 0 && (
          <span
            data-testid="my-devices-strike"
            className={
              'px-2 py-0.5 rounded border ' +
              (strike === 2
                ? 'bg-orange-950/60 border-orange-500/60 text-orange-200'
                : 'bg-yellow-950/60 border-yellow-500/60 text-yellow-200')
            }
          >
            {strike === 2 ? 'FINAL WARNING · one more device = ban' : 'Warning 1 of 2 issued'}
          </span>
        )}
        {overLimit && (
          <span className="px-2 py-0.5 rounded border bg-red-950/60 border-red-500/60 text-red-200">
            Over the limit — revoke a device below
          </span>
        )}
      </div>

      {err && (
        <p className="text-red-400 text-xs bg-red-950/30 border border-red-800/50 rounded px-3 py-2" data-testid="my-devices-error">
          {err}
        </p>
      )}

      {loading && !data && (
        <div className="text-zinc-500 text-sm py-4"><Loader2 className="h-4 w-4 animate-spin inline mr-2" /> Loading devices…</div>
      )}

      {!loading && items.length === 0 && (
        <p className="text-zinc-500 text-sm">No devices seen yet.</p>
      )}

      <ul className="divide-y divide-zinc-900 border border-zinc-900 rounded" data-testid="my-devices-list">
        {items.map((d) => (
          <li key={d.client_id} className="flex items-start gap-3 px-3 py-3" data-testid={`my-device-row-${d.client_id}`}>
            <Monitor className={'h-4 w-4 mt-0.5 shrink-0 ' + (d.is_current ? 'text-emerald-400' : 'text-zinc-500')} />
            <div className="flex-1 min-w-0">
              <p className="text-white text-sm font-black flex items-center gap-2 flex-wrap">
                {d.label}
                {d.is_current && (
                  <span
                    data-testid={`my-device-current-${d.client_id}`}
                    className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-950/70 border border-emerald-600/60 text-emerald-300 uppercase tracking-widest"
                  >
                    This device
                  </span>
                )}
              </p>
              <p className="text-zinc-500 text-[11px] mt-0.5">
                First seen {fmt(d.first_seen)} · Last active {fmt(d.last_seen)}
              </p>
              <p className="text-zinc-600 text-[10px] mt-0.5 font-mono truncate" title={d.user_agent}>
                {(d.last_ip || d.first_ip) && <>IP {(d.last_ip || d.first_ip)} · </>}{d.user_agent}
              </p>
            </div>
            <button
              type="button"
              disabled={d.is_current || busyCid === d.client_id}
              onClick={() => revoke(d.client_id)}
              data-testid={`my-device-revoke-${d.client_id}`}
              title={d.is_current ? "Can't revoke the device you're signed in on" : 'Sign this device out of your account'}
              className={
                'shrink-0 inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-widest px-2.5 py-1.5 rounded border ' +
                (d.is_current
                  ? 'border-zinc-800 text-zinc-600 cursor-not-allowed'
                  : 'border-red-700/60 text-red-300 hover:bg-red-950/60')
              }
            >
              {busyCid === d.client_id ? <Loader2 className="h-3 w-3 animate-spin" /> : <Trash2 className="h-3 w-3" />}
              Revoke
            </button>
          </li>
        ))}
      </ul>

      <p className="text-[10px] text-zinc-600 leading-relaxed">
        A &quot;device&quot; is a browser on a computer or phone. Clearing cookies, using
        incognito, or switching browsers makes a device look new. That&apos;s why the
        policy is 3 devices in 14 days — not 3 forever. Warnings and the ban ladder
        (Warning 1 → Final Warning → Ban) are recorded on your account and stay
        with you across resets.
      </p>
    </section>
  );
};

export default MyDevicesCard;
